import Coach from '@/models/coach/coach.model';
import APIService from '../api.service';

export interface SpecialityResponse {
  data: [
    {
      speciality_id: string;
      speciality_name: string;
    },
  ];
}

export interface CreateCoachData {
  coach_name?: string;
  coach_lastname?: string;
  coach_email?: string;
  coach_headline?: string;
  coach_bio?: string;
  scheduling_link?: string;
  license_number?: string;
  specialities_description?: string;
  years_of_experience?: number;
  client_count?: string;
  price_per_hour?: number;
  speciality_ids?: string[];
  free_session_scheduling_link?: string;
  free_block_session_link?: string;
  individual_session_link?: string;
  coach_level: number;
  is_instructor: boolean;
}

export interface CoachListItem {
  coach_id: string;
  coach_name: string;
  coach_lastname: string;
  coach_photo: string;
  coach_headline: string;
  specialities: {
    speciality_id: string;
    speciality_name: string;
  }[];
  years_of_experience: number;
  price_per_hour: number;
  average_rating: number;
  total_reviews: number;
}

export interface CoachListResponse {
  data: CoachListItem[];
}

export interface CreateCoachResponse {
  success: boolean;
  data?: Coach;
  message?: string;
}

export interface UpdateCoachResponse {
  success: boolean;
  data?: Coach;
  message?: string;
}

class CoachService {
  public async create(
    data: CreateCoachData,
    files?: File[],
  ): Promise<CreateCoachResponse> {
    const formData = new FormData();

    // Add coach data
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (key === 'speciality_ids' && Array.isArray(value)) {
          value.forEach((id) => formData.append('speciality_ids[]', id));
        } else {
          formData.append(key, value.toString());
        }
      }
    });

    // Add files (first file is coach_photo)
    if (files && files.length > 0) {
      files.forEach((file) => {
        formData.append('files', file);
      });
    }

    return APIService.post<CreateCoachResponse>('/coaches', formData);
  }

  public async update(
    id: string,
    data: CreateCoachData,
    files?: File[],
  ): Promise<UpdateCoachResponse> {
    const formData = new FormData();

    // Add coach data
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (key === 'speciality_ids' && Array.isArray(value)) {
          value.forEach((id) => formData.append('speciality_ids[]', id));
        } else {
          formData.append(key, value.toString());
        }
      }
    });

    // Add files (first file is coach_photo)
    if (files && files.length > 0) {
      files.forEach((file) => {
        formData.append('files', file);
      });
    }

    return APIService.put<UpdateCoachResponse>(`/coaches/${id}`, formData);
  }

  public async getAll(): Promise<CoachListResponse> {
    return APIService.get<CoachListResponse>('/coaches');
  }

  public async getById(id: string): Promise<Coach> {
    return APIService.get<Coach>(`/coaches/${id}`);
  }

  public async getSpecialities(): Promise<SpecialityResponse> {
    return APIService.get<SpecialityResponse>('/coaches/specialities');
  }
}

export default new CoachService();
